/**
 * Turn a dead URL into the two things this archive can actually act on: a
 * legacy browse page it still recognises, and a query its known-item resolver
 * can answer.
 *
 * WHY THIS IS A SEPARATE, TESTED MODULE
 *
 * This is the only guessing the 404 page does, and guessing wrong is expensive
 * here. A confident "Did you mean DOE-STD-1090-2007" on a URL that meant
 * something else teaches a reader that our document numbers cannot be trusted
 * — on the one page whose entire job is to rebuild trust after a dead link.
 * Pure functions with a test file are how that stays checked:
 * `recoverPath.test.js` runs against paths taken from the edge access log, not
 * invented ones.
 *
 * WHAT THE AUDIENCE ACTUALLY TYPES
 *
 * Measured, three independent instruments agreeing: Search Console (every top
 * query is a document number), the on-site query log (71% of real queries are
 * a document identifier or a version-history route), and the 404 URLs
 * themselves. So the recovery strategy is: get the identifier out of the path,
 * and let the backend resolver do the rest. Nothing here tries to be clever
 * about titles.
 */

/**
 * Path segments that are structure, not content.
 *
 * Load-bearing: `/standards/doe-std-1020-2016` de-slugified whole becomes
 * "standards doe std 1020 2016", and that leading noise word is enough to
 * push the resolver off the right answer — measured, it returned
 * DOE-HDBK-6004-99. Dropping the segment returns DOE-STD-1020-2016 alone.
 */
const ROUTE_SEGMENTS = new Set([
  'standards',
  'directives',
  'nnsa',
  'documents',
  'document',
  'delegations',
  'glossary',
  'people',
  'references',
  'related-items',
  'search',
  'news',
  'content',
  'node',
  'file',
  'files',
  'view',
]);

/** Extensions the legacy sites served documents under. */
const FILE_SUFFIX = /\.(pdf|html?|aspx|php|jsp|docx?|xlsx?)$/i;

/**
 * Plone's id-collision suffix. `/…-oversight-2` is the SECOND object to want
 * that id, not a document numbered 2 — leaving it in makes the trailing digit
 * look like part of the identifier.
 */
const DEDUP_SUFFIX = /-\d$/;

/** How many resolver hits the page will ever show. */
export const MAX_HITS = 4;

export function pathSegments(pathname) {
  return String(pathname || '')
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .filter(Boolean);
}

/** A slug, as words: `doe-std-1020-2016.pdf` -> `doe std 1020 2016`. */
export function slugToTerms(slug) {
  return String(slug || '')
    .replace(FILE_SUFFIX, '')
    .split(/[-_+.\s]+/)
    .filter(Boolean)
    .join(' ');
}

/** Every digit in a string, in order, separators dropped. */
export function digitsOf(value) {
  return String(value || '').replace(/\D+/g, '');
}

/**
 * The identifier head: tokens up to and including the last one containing a
 * digit.
 *
 * Document slugs put the number first and the title after it, so the head is
 * usually the identifier and the tail is prose that dilutes the match. Both
 * are tried (see `recoverQueries`) because neither wins every time: the head
 * is what finds DOE M 232.1-1A under
 * `/documents/doe-m-2321-1a-occurrence-reporting-and-processing`, and the full
 * string is what finds NAP-21 under
 * `/documents/nap-21-transformational-governance-and-oversight`.
 */
export function identifierHead(terms) {
  const tokens = String(terms || '')
    .split(' ')
    .filter(Boolean);
  let last = -1;
  tokens.forEach((token, i) => {
    if (/\d/.test(token)) last = i;
  });
  return last < 0 ? '' : tokens.slice(0, last + 1).join(' ');
}

/**
 * The queries to put to the resolver, most specific first, deduplicated.
 *
 * Returns `{ queries, primary }` where `primary` is what the search box is
 * seeded with — the full de-slugified string, because that is what a person
 * would recognise as "what I was looking for".
 */
export function recoverQueries(pathname) {
  const segments = pathSegments(pathname);
  while (segments.length > 1 && ROUTE_SEGMENTS.has(segments[0].toLowerCase())) {
    segments.shift();
  }
  const slug = String(segments[segments.length - 1] || '').replace(
    FILE_SUFFIX,
    '',
  );
  const full = slugToTerms(slug.replace(DEDUP_SUFFIX, ''));
  if (!full) return { queries: [], primary: '' };
  const head = identifierHead(full);
  const queries = head && head !== full ? [head, full] : [full];
  return { queries, primary: full };
}

/**
 * The series a document number belongs to: its alphabetic prefix plus the
 * major number. `DOE O 414.1F` -> `DOEO:414`, `DOE-STD-1020-2016` ->
 * `DOESTD:1020`, `SD 205.1A Admin Chg. 1` -> `SD:205`.
 */
export function seriesKey(docid) {
  const tokens = String(docid || '')
    .split(/[\s\-_/]+/)
    .filter(Boolean);
  const alpha = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (/^\d/.test(token)) {
      return `${alpha.join('')}:${token.match(/^\d+/)[0]}`;
    }
    if (/^[A-Za-z]+$/.test(token)) alpha.push(token.toUpperCase());
  }
  return null;
}

/**
 * Is this hit a confident answer to that query, or merely the closest thing
 * the archive has?
 *
 * Only the document numbers are compared, and only as equality or a prefix
 * relation. A substring test would be too generous: `/directives/002-007.pdf`
 * has digits "002007", and "10902007" (DOE-STD-1090-2007) contains "007" — a
 * match no reader would accept.
 *
 * The prefix arm additionally needs three digits on its shorter side. Two is
 * not enough evidence to claim anything: measured on
 * `/documents/nap-21-transformational-governance-and-oversight-2`, "21"
 * prefix-matched delegation order 21.00 "Designation to the Under Secretary",
 * which was printed as a confident answer beside the correct NAP-21. Exact
 * equality is exempt, which is what keeps NAP-21 itself.
 *
 * This decides both the heading and, when anything is confident, which rows
 * are shown at all — see NotFoundView.
 */
export function isConfidentMatch(query, docid) {
  const q = digitsOf(query);
  const d = digitsOf(docid);
  if (q.length < 2 || d.length < 2) return false;
  if (q === d) return true;
  if (Math.min(q.length, d.length) < 3) return false;
  return q.startsWith(d) || d.startsWith(q);
}

/**
 * A legacy browse page this archive still answers, under a new address.
 *
 * The consolidation deleted ~82 variant pages and replaced each with a
 * `?preset=<id>` deep link on the family's canonical page, keeping the LEGACY
 * page id as the preset id. So the dead slug is the lookup key, exactly — no
 * guessing involved, which is why this is offered above the resolver hits
 * when it fires.
 */
export function presetFor(pathname, siteId, registry) {
  const segments = pathSegments(pathname);
  const slug = String(segments[segments.length - 1] || '')
    .replace(FILE_SUFFIX, '')
    .toLowerCase();
  if (!slug || !registry) return null;
  const families = registry[siteId] || {};
  const familyNames = Object.keys(families);
  for (let i = 0; i < familyNames.length; i += 1) {
    const family = familyNames[i];
    const preset = families[family] && families[family][slug];
    if (preset) {
      return {
        to: `/${family}?preset=${preset.id}`,
        label: preset.label,
        id: preset.id,
      };
    }
  }
  return null;
}

/**
 * Reduce a `@family-timeline` payload to the one line worth printing, or
 * null.
 *
 * THE OVER-MERGE GUARD IS THE POINT OF THIS FUNCTION.
 *
 * Version families are inferred from supersession relations, and the
 * inference over-merges: measured 2026-08-20, 5 of 40 recent directives
 * (12.5%) resolve to a canonical in a completely different series. DOE O
 * 420.1D "Facility Safety" lands in a 164-member family canonicalized as DOE
 * O 440.2D "Aviation Management". Median family size is a healthy 8, so this
 * is a tail defect, not a broken model — but "and 163 earlier versions" under
 * the wrong title is precisely the sort of confident wrongness this page
 * cannot afford.
 *
 * So the chain line is printed only when the family's canonical is in the
 * SAME series as the document it is being printed for. That suppresses every
 * measured over-merge and costs nothing on a healthy family, whose canonical
 * is the document itself or a sibling revision. Members may legitimately span
 * series (renumbering happens) — only the canonical is checked.
 *
 * The underlying defect is tracked separately; this guard is what makes the
 * page safe to ship in the meantime.
 */
export function summariseChain(docid, timeline) {
  if (!timeline || !Array.isArray(timeline.items)) return null;
  const count = Number(timeline.count) || timeline.items.length;
  if (count < 2) return null;

  const canonical = seriesKey(timeline.canonical_docid);
  const own = seriesKey(docid);
  if (!canonical || !own || canonical !== own) return null;

  const years = timeline.items
    .map((item) => String(item.issue_date || item.date || '').slice(0, 4))
    .filter((year) => /^\d{4}$/.test(year))
    .sort();
  if (years.length < 2) return null;

  return { count, from: years[0], to: years[years.length - 1] };
}
